import React, { useCallback, useEffect, useState } from "react";
import Step1Form from "./Form";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  getEditExternalResource,
  getProfileNumberExternal,
  getResourcePosition,
  getResourceStatusOptions,
  getTreeMenueOptions,
  requestProfileChange,
  SaveUserProfileExternal,
  UpdateUserProfileExternal,
} from "../../../../../redux/Thunk/externalResourceThunk";
import { CurrencyCode } from "../../../../../redux/Thunk/AdminModule/AdminThunk";
import { getTimezone, SpecializationDropdown } from "../../../../../redux/Thunk/ClientModule/ClientThunk";
import { validateFields } from "../../../../../Helper/ValidateFields";
import { debounce } from "lodash";
import Loader from "../../../../../Components/Ui_elements/Loader";
import APP_CONFIG_URL_ENV from "../../../../../env-config";
import { showSnackbar } from "../../../../../redux/Slice/snackbar/snackbarSlice";
import { setloginUserData } from "../../../../../redux/Slice/authSlice/loginSlice";
import { formatInternationalMobile, parseIMobile } from "../../../../../Helper/phone";

const Step1 = ({ setisFreelancer, buttonref, isFreelancer, registerSubmit }) => {
  const dispatch = useDispatch();
  const { user } = useSelector(state => state.root.authUser);
  const { currencyCode } = useSelector(state => state.root.AdminModule);
  const { country, specialization_list } = useSelector(state => state.root.ClientModule);
  const { resourceStatusList } = useSelector(state => state.root.ExternalResource);
  const { loading } = useSelector(state => state.root.loading);
  const { profile_number_external, edit_external_resource_data } = useSelector(
    state => state.root.ExternalResource
  );
  const [profilePreview, setProfilePreview] = useState(null);
  const [absentPeriods, setAbsentPeriods] = useState([]); // State for absent periods
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const cId = searchParams.get("c_id");
  const ID = params?.id ?? cId; // this cId condition need to add for updating data

  // Profile request change modal
  const isEditMode = Boolean(ID || cId);
  const isLinguist = user?.iFkUserTypeId === 2; // linguist
  const lockedFields = isEditMode && isLinguist ? ["firstName", "lastName", "email", "freelancer"] : [];
  const [changeRequestOpen, setChangeRequestOpen] = useState(false);
  const [requestedField, setRequestedField] = useState("");
  const [requestedValue, setRequestedValue] = useState("");
  const [oldValue, setOldValue] = useState("");
  const [newValue, setNewValue] = useState("");
  const [reason, setReason] = useState("");

  const initialState = {
    profileNumber: ID
      ? `${edit_external_resource_data?.data?.iResourceNumber || ""}`
      : `${profile_number_external}`,
    resourceStatus: "",
    firstName: "",
    lastName: "",
    gender: "",
    dateOfBirth: "",
    absentFrom: "",
    absentTo: "",
    username: "",
    password: "",
    currency: "",
    nokcurrency: "",
    norwayCurrency: false,
    email: "",
    secondaryEmail: "",
    memo: "",
    website: "",
    companyName: "",
    mobile: {
      countryIso: "US",
      phoneCode: "1",
      number: "",
      nicename: "",
    },
    skype: "",
    profilePicture: null,
    vAddress1: "",
    country: "",
    state: "",
    vCity1: "",
    zipcode: "",
    timezone: "",
    specialization: [],
  };
  const formFields = [
    { name: "firstName", label: "First Name" },
    { name: "lastName", label: "Last Name" },
    { name: "email", label: "Email" },
    { name: "currency", label: "Currency" },
    { name: "resourceStatus", label: "Resource Status" },
  ];
  const [formData, setFormData] = useState(initialState);
  const [isProfilePictureChanged, setIsProfilePictureChanged] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const request = { id: user?.iUserId };
    if (ID || cId) {
      dispatch(getEditExternalResource(ID ? ID : cId));
    }
    dispatch(getResourceStatusOptions());
    dispatch(getResourcePosition(request));
    dispatch(getTreeMenueOptions());
    dispatch(CurrencyCode());
    dispatch(SpecializationDropdown());
  }, [dispatch, user, ID, cId]);

  useEffect(() => {
    const fetchProfileNumber = async () => {
      // Only fetch if we don't have an ID (creating new resource) 
      // and profile_number_external isn't already set
      if (!ID && !profile_number_external) {
        await dispatch(getProfileNumberExternal(2));
      }

      // Update form data when we have a profile number (either from API or edit data)
      const newProfileNumber = ID
        ? edit_external_resource_data?.data?.iResourceNumber
        : profile_number_external;

      if (newProfileNumber) {
        setFormData(prevState => ({
          ...prevState,
          profileNumber: `${newProfileNumber}`,
        }));
      }
    };

    fetchProfileNumber();
  }, [dispatch, user?.iFkUserTypeId, ID, profile_number_external, edit_external_resource_data?.data?.iResourceNumber]);

  useEffect(() => {
    if (!edit_external_resource_data?.data) return;
    // when edit internal resource then data will get set from here
    if ((edit_external_resource_data && ID) || cId) {
      const {
        vFirstName,
        vLastName,
        eUserStatus,
        iGender,
        dtBirthDate,
        vEmailAddress,
        org_pass,
        vSkypeName,
        iMobile,
        tabPermission,
        is_available,
        vProfilePic,
        S3vProfilePic,
        vTimeZone,
        vSecondaryEmailAddress,
        vWebsite,
        companyName,
        tMemo,
        freelance_currency,
        second_currency,
        freelancer,
      } = edit_external_resource_data?.data; // Destructuring

      const mobileParsed = parseIMobile(iMobile);
      const absentPeriodsData =
        is_available && typeof is_available === "string" ? JSON.parse(is_available) : [];
      const tabsPermissionData =
        tabPermission && typeof tabPermission === "string"
          ? Object.keys(JSON.parse(tabPermission))
          : [];

      const address1Detail = edit_external_resource_data?.data?.address1Detail
        ? JSON.parse(edit_external_resource_data?.data?.address1Detail)
        : [];
      const addressFields = address1Detail.reduce((acc, field) => {
        acc[field.id] = field.value;
        return acc;
      }, {});

      // Handle profile picture
      let profileImage = null;
      if (S3vProfilePic) {
        profileImage = S3vProfilePic;
      } else {
        const rawProfilePic = typeof vProfilePic === "string" ? vProfilePic.trim() : "";
        const hasValidProfilePic =
          rawProfilePic.length > 0 &&
          rawProfilePic.toLowerCase() !== "null" &&
          rawProfilePic.toLowerCase() !== "undefined";

        if (hasValidProfilePic) {
          profileImage = rawProfilePic.startsWith("data:image")
            ? rawProfilePic // Base64 string
            : rawProfilePic.startsWith("http")
              ? rawProfilePic // Full URL
              : `${APP_CONFIG_URL_ENV.REACT_APP_FILEPATH}profilePic/${rawProfilePic}`; // Filename, prepend base URL
        }
      }
      setisFreelancer(freelancer === "freelancer" ? "freelancer" : "translation");
      setFormData({
        profileNumber: `${edit_external_resource_data?.data?.iResourceNumber}`,
        resourceStatus: eUserStatus,
        firstName: vFirstName,
        lastName: vLastName,
        gender: iGender == "1" ? "Male" : iGender == "2" ? "Female" : "Other",
        dateOfBirth: dtBirthDate,
        absentFrom: "",
        absentTo: "",
        username: `${vFirstName} ${vLastName}`,
        password: org_pass,
        email: vEmailAddress,
        secondaryEmail: vSecondaryEmailAddress,
        freelancer: freelancer,
        website: vWebsite,
        memo: tMemo,
        companyName: companyName,
        currency: freelance_currency?.split(",")?.[0] || "EUR",
        nokcurrency: second_currency,
        norwayCurrency: second_currency === "NOK" ? true : false,
        mobile: {
          countryIso: mobileParsed?.countryFlagClass?.split(" ")[1]?.toUpperCase() || "US",
          phoneCode: mobileParsed?.phoneCode || "1",
          number: mobileParsed?.nationalNumber || "",
          nicename: mobileParsed?.countryTitle || "",
        },
        vAddress1: addressFields["address1_street_number"] || "",
        country: addressFields["address1_country"] || "",
        state: addressFields["address1_administrative_area_level_1"] || "",
        vCity1: addressFields["address1_locality"] || "",
        zipcode: addressFields["address1_postal_code"] || "",
        timezone: vTimeZone,
        skype: vSkypeName,
        tabsPermission: tabsPermissionData,
        profilePicture: profileImage,
        specialization: edit_external_resource_data?.data?.specialization
          ? String(edit_external_resource_data.data.specialization)
              .split(",")
              .filter(Boolean)
              .map(id => parseInt(id, 10))
          : [],
      });

      setAbsentPeriods(
        absentPeriodsData.map((period, index) => ({
          from: period.dateFrom,
          to: period.dateTo,
          id: `${period.dateFrom}-${period.dateTo}-${index}`,
          //id: new Date().getTime(),
        }))
      );

      if (profileImage) {
        setProfilePreview(profileImage);
      }
    }
  }, [edit_external_resource_data?.data, ID, cId]);

  const handleFileChange = event => {
    event.preventDefault();
    event.stopPropagation();

    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = e => {
        const base64String = e.target.result; // This is the base64 string
        setFormData(prevData => ({
          ...prevData,
          profilePicture: base64String, // Store the base64 string
        }));
        setProfilePreview(base64String); // Set the preview with the base64 string
        setIsProfilePictureChanged(true);
      };
      reader.readAsDataURL(file); // Convert the file to a base64 string
    }
  };

  const tabsPermissionOption = () => {
    if (user?.tab_sortedorder) {
      return JSON.parse(user.tab_sortedorder)?.map(option => ({
        value: option?.tabName,
        label: option?.tabName,
      }));
    }
    return [];
  };

  const fetchTimezone = useCallback(
    debounce(city => {
      dispatch(getTimezone(city)).then(response => {
        if (response?.meta?.arg.length > 0) {
          setFormData(prevData => ({
            ...prevData,
            timezone: response?.payload?.data?.timeZone || "",
          }));
        } else {
          setFormData(prevData => ({
            ...prevData,
            timezone: "",
          }));
        }
      });
    }, 500),
    [dispatch]
  );

  const handleChange = e => {
    const { name, value } = e.target;
    if (name === "mobile") {
      const rawValue = value?.number ?? ""; // fallback to empty string
      const digitsOnly = rawValue.replace(/\D/g, "");

      setFormData(prevData => ({
        ...prevData,
        [name]: {
          ...value,
          number: digitsOnly, // update only the number part, keep rest intact
        },
      }));
    } else {
      // Update the form data
      setFormData(prevState => {
        const updatedFormData = {
          ...prevState,
          [name]: value,
        };
        if (name === "vCity1") {
          fetchTimezone(value);
        }
        return updatedFormData;
      });
    }
    // Clear the error for the specific field
    setErrors(prevErrors => ({
      ...prevErrors,
      [name]: "",
    }));
  };

  const handleChipDelete = (value, field) => {
    const newValues = formData[field].filter(item => item !== value);
    setFormData(prevState => ({
      ...prevState,
      [field]: newValues,
    }));
  };

  const handleMultiSelectChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: typeof value === "string" ? value.split(",") : value,
    }));
    setErrors(prevErrors => ({
      ...prevErrors,
      [name]: "",
    }));
  };

  const handleAddAbsentPeriod = () => {
    dispatch(
      showSnackbar({
        message: "Manage absences on the resource profile Absent List tab.",
        type: "info",
      })
    );
  };

  const handleDeleteAbsentPeriod = id => {
    setAbsentPeriods(prev => prev.filter(period => period.id !== id));
  };

  const handleSubmit = async (e) => {
    const newErrors = validateFields(formData, formFields);
    // Validate form
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Constructing the custom payload
    const payload = {
      address1Detail: JSON.stringify([
        { id: "address1_street_number", value: formData.vAddress1 },
        { id: "address1_locality", value: formData.vCity1 },
        { id: "address1_administrative_area_level_1", value: formData.state },
        { id: "address1_country", value: formData.country },
        { id: "address1_postal_code", value: formData.zipcode },
      ]),
      address2Detail: "[]", // Assuming address2 is not used
      companyName: formData.companyName,
      created_by: user?.iUserId,
      dtBirthDate: formData.dateOfBirth,
      dtCreationDate: new Date().toLocaleString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      }),
      eUserStatus: formData.resourceStatus,
      freelance_currency: formData.currency,
      freelancer: isFreelancer,
      iGender: formData.gender === "Male" ? "1" : formData.gender === "Female" ? "2" : "3",
      iMobile: JSON.stringify({
        countryTitle: `${formData.mobile.nicename}`,
        countryFlagClass: `iti-flag ${formData.mobile.countryIso.toLowerCase()}`,
        mobileNumber: formatInternationalMobile({
          phoneCode: formData.mobile.phoneCode,
          number: formData.mobile.number,
        }),
      }),
      iResourceNumber: formData.profileNumber,
      org_pass: formData.password,
      second_currency: formData.nokcurrency,
      tMemo: formData.memo,
      vAddress1: formData.vAddress1,
      vEmailAddress: formData.email,
      vFirstName: formData.firstName,
      vLastName: formData.lastName,
      vPassword: formData.password,
      vSecondaryEmailAddress: formData.secondaryEmail,
      vSkypeName: formData.skype,
      vTimeZone: formData.timezone,
      vTimeZoneCity: formData.vCity1,
      vUserName: `${formData.firstName} ${formData.lastName}`,
      vWebsite: formData.website,
      specialization: formData.specialization?.length
        ? formData.specialization.join(",")
        : "",
    };
    // Dispatch the action to save the payload
    // Conditionally add image to payload only if it has changed
    if (formData.profilePicture && isProfilePictureChanged) {
      payload.image = formData.profilePicture;
    }
    if (ID) {
      // If editing an existing resource
      const res = await dispatch(
        UpdateUserProfileExternal({
          ...payload,
          iUserId: ID,
          activation_token: edit_external_resource_data?.data?.activation_token,
        })
      ).unwrap();

      // If user updated their own profile, refresh auth user immediately (redux + persisted).
      if (Number(ID) === Number(user?.iUserId) && res?.userData) {
        dispatch(setloginUserData({ user: res.userData, isAuthenticated: true }));
      }
      setIsProfilePictureChanged(false);
    } else {
      setIsProfilePictureChanged(false);
      // If creating a new resource
      const response = await dispatch(SaveUserProfileExternal(payload));
      const newId = response?.payload?.data?.iUserId;
      
      if (response?.payload?.status === 200 && newId) {
        setTimeout(()=>{
          navigate(`${location.pathname}?c_id=${newId}&step=${1}`);
        },500)
      } else {
        return false;
      }
    }
    return true;
  };

  const handleChangeRadio = event => {
    setisFreelancer(event.target.value);
  };

  const currencyOptions = Object?.entries(currencyCode)?.map(([key, currency]) => ({
    label: `${key}`,
    value: `${key}`,
  }));

  const countryOptions = country?.map(c => ({
    label: c.name,
    value: c.nicename,
  }));

  const specializationOptions = specialization_list?.map(item => ({
    label: item.name,
    value: item.id,
  }));

  useEffect(() => {
    registerSubmit(handleSubmit);
  }, [formData, isFreelancer]);

  
  // start Profile request change modal
  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const submitChangeRequest = () => {
    if (!newValue || !reason) {
      dispatch(
        showSnackbar({
          message: "New value and reason are required",
          type: "error",
        })
      );
      return;
    }
    if (requestedField === "email" && !isValidEmail(newValue)) {
      dispatch(
        showSnackbar({
          message: "Please enter a valid email address",
          type: "error",
        })
      );
      return;
    }

    dispatch(
      requestProfileChange({
        user_id: ID,
        field_name: requestedField,
        old_value: oldValue,
        new_value: newValue,
        reason,
      })
    );

    setChangeRequestOpen(false);
  };

  const openChangeRequest = (field) => {
    setRequestedField(field);

    const fieldValueMap = {
      "firstName": formData.firstName,
      "lastName": formData.lastName,
      "email": formData.email,
      "freelancer": formData.freelancer,
    };

    const currentValue = fieldValueMap[field] || "";

    setOldValue(currentValue);
    setNewValue("");     // user must type new value
    setReason("");       // reset reason
    setChangeRequestOpen(true);
  };
  // end Profile request change modal
  
  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <Step1Form
          formData={formData}
          setFormData={setFormData}
          errors={errors}
          handleChange={handleChange}
          handleSubmit={handleSubmit}
          resourceStatusList={resourceStatusList}
          handleChipDelete={handleChipDelete}
          handleMultiSelectChange={handleMultiSelectChange}
          specializationOptions={specializationOptions}
          handleAddAbsentPeriod={handleAddAbsentPeriod}
          absentPeriods={absentPeriods}
          handleDeleteAbsentPeriod={handleDeleteAbsentPeriod}
          handleFileChange={handleFileChange}
          profilePreview={profilePreview}
          setProfilePreview={setProfilePreview}
          tabsPermissionOption={tabsPermissionOption}
          currencyOptions={currencyOptions}
          countryOptions={countryOptions}
          handleChangeRadio={handleChangeRadio}
          isFreelancer={isFreelancer}

          /* 🔐 NEW PROPS */
          lockedFields={lockedFields}
          openChangeRequest={openChangeRequest}
          changeRequestOpen={changeRequestOpen}
          setChangeRequestOpen={setChangeRequestOpen}
          requestedField={requestedField}

          oldValue={oldValue}
          newValue={newValue}
          reason={reason}
          setNewValue={setNewValue}
          setReason={setReason}
          onSubmitChangeRequest={submitChangeRequest}

        />
      )}
    </>
  );
};

export default Step1;
